import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
    Badge,
    Box,
    Flex,
    IconButton,
    Input,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Check, Download, RotateCcw, Trash2, X } from 'lucide-react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { useAliasPgpStore, type AliasEntry } from '../../state/useAliasPgpStore';
import {
    DEFAULT_ALIASES_PGP,
    YAP_ALIASES_VERSION,
    findDuplicateAliases,
    getKnownAliasTargetNames,
    isBuiltinCommandName,
    parsePgpEntries,
    serializePgp,
} from '../../utils/aliasPgp';
import { parseAcadPgp, readAcadPgpFile } from '../../utils/acadPgp';
import { pickLocalFile, downloadTextFile } from '../../services/fileSystem/fileAccess';
import { writeAppFile } from '../../services/fileSystem/desktopIo';
import { notify } from '../../utils/notify';

/** One editable row in the alias table. */
interface DraftRow extends AliasEntry {
    key: number;
}

/**
 * ALIASEDIT dialog (Faz 9.6, OCS alias_editor.rs parity): row table with an
 * inline draft add row (✓/✕), per-row delete, duplicate-alias and
 * unknown-command flags, confirmed reset to the built-in set, close-without-
 * save protection ("Discard & Close" / "Keep editing"), Apply / Apply & Exit.
 * Applying persists to the yap-aliases store and mirrors the file to
 * `yap/aliases.pgp` in the app-data tree (best-effort on desktop).
 */
export function AliasEditDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const { t } = useTranslation();
    const initialRef = useRef<HTMLInputElement>(null);

    const storedEntries = useAliasPgpStore((s) => s.entries);
    const setStore = useAliasPgpStore((s) => s.setStore);

    const [rows, setRows] = useState<DraftRow[]>([]);
    const [dirty, setDirty] = useState(false);
    const [draftAlias, setDraftAlias] = useState('');
    const [draftCommand, setDraftCommand] = useState('');
    const [confirmReset, setConfirmReset] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const [booted, setBooted] = useState(false);
    const nextKey = useRef(1);

    // Load the persisted rows once per open (keeps editor state on close).
    if (isOpen && !booted) {
        setBooted(true);
        setRows(storedEntries.map((e) => ({ ...e, key: nextKey.current++ })));
        setDirty(false);
    }
    if (!isOpen && booted) {
        setBooted(false);
        setConfirmReset(false);
        setConfirmClose(false);
    }

    const knownNames = useMemo(() => getKnownAliasTargetNames(), [isOpen]);

    const conflicts = useMemo(() => findDuplicateAliases(rows), [rows]);
    const unknownRows = useMemo(() => {
        const set = new Set<number>();
        for (const row of rows) {
            if (!row.command) continue;
            if (!knownNames.has(row.command.toUpperCase().replace(/^\*+/, ''))) set.add(row.key);
        }
        return set;
    }, [rows, knownNames]);
    // Aliases that shadow a built-in command name: the parser layer resolves
    // built-ins first, so these rows flag as inert unless the target matches.
    const shadowedRows = useMemo(() => {
        const set = new Set<number>();
        for (const row of rows) {
            if (row.alias && isBuiltinCommandName(row.alias)) set.add(row.key);
        }
        return set;
    }, [rows]);

    const updateRow = useCallback((key: number, field: 'alias' | 'command', value: string) => {
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value.toUpperCase() } : r)));
        setDirty(true);
    }, []);

    const deleteRow = useCallback((key: number) => {
        setRows((prev) => prev.filter((r) => r.key !== key));
        setDirty(true);
    }, []);

    const addDraft = useCallback(() => {
        const alias = draftAlias.trim().toUpperCase().replace(/^\*+/, '');
        const command = draftCommand.trim().toUpperCase().replace(/^\*+/, '');
        if (!alias || !command) return;
        setRows((prev) => [...prev, { alias, command, key: nextKey.current++ }]);
        setDraftAlias('');
        setDraftCommand('');
        setDirty(true);
    }, [draftAlias, draftCommand]);

    const apply = useCallback((): number => {
        const clean = rows
            .map(({ alias, command }) => ({
                alias: alias.trim().toUpperCase(),
                command: command.trim().toUpperCase().replace(/^\*+/, ''),
            }))
            .filter((r) => r.alias && r.command);
        // Deleted built-ins land in `removed` so migrations never resurrect
        // them; custom rows simply live in entries.
        const draftAliases = new Set(clean.map((r) => r.alias.toUpperCase()));
        const builtin = parsePgpEntries(DEFAULT_ALIASES_PGP);
        const removedBuiltins = builtin
            .map((b) => b.alias.toUpperCase())
            .filter((a) => !draftAliases.has(a));
        setStore({
            entries: clean,
            removed: removedBuiltins,
            version: YAP_ALIASES_VERSION,
        });
        // appData mirror (best-effort; desktop host only).
        void writeAppFile('yap/aliases.pgp', serializePgp(clean, YAP_ALIASES_VERSION), true)
            .catch(() => undefined);
        setDirty(false);
        return clean.length;
    }, [rows, setStore]);

    const importPgp = useCallback(async () => {
        const file = await pickLocalFile('.pgp');
        if (!file) return;
        const text = await readAcadPgpFile(file);
        const incoming = parseAcadPgp(text).filter((e) => e.alias && e.command);
        if (incoming.length === 0) {
            notify(t('dialogs.ui.aliasImportFailed', { name: file.name }), 'warning');
            return;
        }
        setRows((prev) => {
            const byAlias = new Map(prev.map((r) => [r.alias.toUpperCase(), r]));
            for (const e of incoming) {
                const key = e.alias.toUpperCase();
                const existing = byAlias.get(key);
                if (existing) {
                    byAlias.set(key, { ...existing, command: e.command.toUpperCase() });
                } else {
                    byAlias.set(key, { alias: e.alias, command: e.command.toUpperCase(), key: nextKey.current++ });
                }
            }
            return [...byAlias.values()];
        });
        setDirty(true);
        notify(t('dialogs.ui.aliasImported', { count: incoming.length, name: file.name }), 'success');
    }, [t]);

    const resetToDefaults = useCallback(() => {
        setConfirmReset(false);
        const builtin = parsePgpEntries(DEFAULT_ALIASES_PGP);
        setRows(builtin.map((e) => ({ ...e, key: nextKey.current++ })));
        setDirty(true);
        setStore({ entries: builtin, removed: [], version: YAP_ALIASES_VERSION });
        notify(t('dialogs.ui.aliasReset'), 'info');
    }, [setStore, t]);

    const handleClose = useCallback(() => {
        if (dirty) {
            setConfirmClose(true);
            return;
        }
        onClose();
    }, [dirty, onClose]);

    return (
        <>
            <AppDialog
                isOpen={isOpen}
                onClose={handleClose}
                title={t('dialogs.ui.aliasEdit')}
                size="lg"
                initialFocusRef={initialRef}
                footer={
                    <>
                        <DialogButton variant="ghost" onClick={() => void importPgp()}>
                            {t('dialogs.ui.aliasImport')}
                        </DialogButton>
                        <DialogButton
                            variant="ghost"
                            onClick={() => downloadTextFile('aliases.pgp', serializePgp(rows, YAP_ALIASES_VERSION))}
                            title={t('dialogs.ui.aliasExported', { name: 'aliases.pgp' })}
                        >
                            <Box as="span" display="inline-flex" alignItems="center" gap={1}>
                                <Download size={14} /> .pgp
                            </Box>
                        </DialogButton>
                        <DialogButton variant="ghost" onClick={() => setConfirmReset(true)}>
                            <Box as="span" display="inline-flex" alignItems="center" gap={1}>
                                <RotateCcw size={14} /> {t('dialogs.ui.aliasResetDefaults')}
                            </Box>
                        </DialogButton>
                        <DialogButton
                            variant="secondary"
                            onClick={() => notify(t('dialogs.ui.aliasApplied', { count: apply() }), 'success')}
                        >
                            {t('dialogs.ui.aliasApply')}
                        </DialogButton>
                        <DialogButton
                            variant="primary"
                            onClick={() => {
                                apply();
                                onClose();
                            }}
                        >
                            {t('dialogs.ui.aliasApplyExit')}
                        </DialogButton>
                    </>
                }
            >
                <VStack align="stretch" spacing={1}>
                    <Flex px={1} gap={2} fontWeight="semibold" fontSize="10px" color="text.secondary" textTransform="uppercase">
                        <Text w="120px" flexShrink={0}>{t('dialogs.ui.aliasColAlias')}</Text>
                        <Text flex="1" minW={0}>{t('dialogs.ui.aliasColCommand')}</Text>
                        <Text w="140px" flexShrink={0}>{t('dialogs.ui.aliasColStatus')}</Text>
                    </Flex>
                    <Box maxH="50vh" overflowY="auto">
                        <VStack align="stretch" spacing={0.5}>
                            {rows.map((row) => {
                                const dup = conflicts.has(row.alias.toUpperCase());
                                const unknown = unknownRows.has(row.key);
                                return (
                                    <Flex key={row.key} px={1} gap={2} align="center" _hover={{ bg: 'bg.hover' }} borderRadius="4px">
                                        <Input
                                            size="sm"
                                            w="120px"
                                            flexShrink={0}
                                            value={row.alias}
                                            onChange={(e) => updateRow(row.key, 'alias', e.target.value)}
                                            fontFamily="mono"
                                        />
                                        <Input
                                            size="sm"
                                            flex="1"
                                            minW={0}
                                            value={row.command}
                                            onChange={(e) => updateRow(row.key, 'command', e.target.value)}
                                            fontFamily="mono"
                                        />
                                        <Flex w="140px" flexShrink={0} gap={1} align="center" flexWrap="wrap">
                                            {dup && <Badge colorScheme="red" size="sm">{t('dialogs.ui.aliasDuplicate')}</Badge>}
                                            {unknown && <Badge colorScheme="orange" size="sm">{t('dialogs.ui.aliasUnknownCommand')}</Badge>}
                                            {shadowedRows.has(row.key) && !dup && (
                                                <Badge colorScheme="yellow" size="sm">{t('dialogs.ui.aliasShadowed')}</Badge>
                                            )}
                                        </Flex>
                                        <IconButton
                                            size="xs"
                                            variant="ghost"
                                            aria-label={t('dialogs.ui.aliasDelete')}
                                            icon={<Trash2 size={12} />}
                                            onClick={() => deleteRow(row.key)}
                                        />
                                    </Flex>
                                );
                            })}
                        </VStack>
                    </Box>
                    {/* Draft add row (OCS draft insertion line) */}
                    <Flex px={1} gap={2} align="center" pt={1} borderTop="1px solid" borderColor="border.base">
                        <Input
                            ref={initialRef}
                            size="sm"
                            w="120px"
                            flexShrink={0}
                            placeholder={t('dialogs.ui.aliasNewRow')}
                            value={draftAlias}
                            onChange={(e) => setDraftAlias(e.target.value)}
                            fontFamily="mono"
                        />
                        <Input
                            size="sm"
                            flex="1"
                            minW={0}
                            placeholder={t('dialogs.ui.aliasNewCommand')}
                            value={draftCommand}
                            onChange={(e) => setDraftCommand(e.target.value)}
                            fontFamily="mono"
                        />
                        <Flex w="140px" flexShrink={0} gap={1} justify="flex-end">
                            <IconButton
                                size="xs"
                                variant="ghost"
                                isDisabled={!draftAlias.trim() || !draftCommand.trim()}
                                aria-label={t('dialogs.ui.aliasAdd')}
                                icon={<Check size={12} />}
                                onClick={addDraft}
                            />
                            <IconButton
                                size="xs"
                                variant="ghost"
                                isDisabled={!draftAlias && !draftCommand}
                                aria-label={t('dialogs.ui.cancel')}
                                icon={<X size={12} />}
                                onClick={() => { setDraftAlias(''); setDraftCommand(''); }}
                            />
                        </Flex>
                    </Flex>
                </VStack>
            </AppDialog>

            {/* Reset confirmation (OCS reset-with-confirm) */}
            <AppDialog
                isOpen={confirmReset}
                onClose={() => setConfirmReset(false)}
                title={t('dialogs.ui.aliasResetDefaults')}
                variant="alert"
                footer={
                    <>
                        <DialogButton variant="secondary" onClick={() => setConfirmReset(false)}>
                            {t('dialogs.ui.cancel')}
                        </DialogButton>
                        <DialogButton variant="destructive" onClick={resetToDefaults}>
                            {t('dialogs.ui.aliasResetDefaults')}
                        </DialogButton>
                    </>
                }
            >
                <Text>{t('dialogs.ui.aliasResetConfirm')}</Text>
            </AppDialog>

            {/* Close-without-save protection (OCS "Discard & close") */}
            <AppDialog
                isOpen={confirmClose}
                onClose={() => setConfirmClose(false)}
                title={t('dialogs.ui.aliasUnsaved')}
                variant="alert"
                footer={
                    <>
                        <DialogButton variant="secondary" onClick={() => setConfirmClose(false)}>
                            {t('dialogs.ui.aliasKeepEditing')}
                        </DialogButton>
                        <DialogButton variant="destructive" onClick={() => { setConfirmClose(false); onClose(); }}>
                            {t('dialogs.ui.aliasDiscardAndClose')}
                        </DialogButton>
                    </>
                }
            >
                <Text>{t('dialogs.ui.aliasUnsaved')}</Text>
            </AppDialog>
        </>
    );
}
